<template>
  <v-dialog
    v-model="dialog"
    :fullscreen="fullscreen"
    :width="fullscreen ? 'auto' : '600px'"
  >
    <v-sheet
      class="h-full"
      color="background"
      :class="{
        'w-full': fullscreen,
      }"
    >
      <div class="h-full pa-4 flex-center flex-col relative">
        <div class="absolute top-2 right-2">
          <v-btn icon="close" variant="outlined" @click="dialog = false" />
        </div>
        <div class="md:max-w-400px" :class="{ 'pt-20 pb-15': !fullscreen }">
          <v-card
            class="pa-10 flex-center flex-col relative overflow-visible!"
            rounded="xl"
            variant="flat"
            border
          >
            <LogoChip
              class="inline-block absolute left-50% top-0 transform translate-x-[-50%] translate-y-[-50%]"
              :class="{
                'top-0': fullscreen,
                'top-2': !fullscreen,
              }"
            />

            <div v-if="title || $slots.title" class="heading-2">
              <slot name="title">
                {{ title }}
              </slot>
            </div>
            <div v-if="subtitle || $slots.subtitle" class="caption">
              <slot name="subtitle">
                {{ subtitle }}
              </slot>
            </div>

            <QRCode
              v-if="dialog && code"
              ref="qrCodeRef"
              class="mt-10"
              :width="200"
              :height="200"
              :value="code"
              :qr-options="{
                typeNumber: 0,
                mode: 'Byte',
                errorCorrectionLevel: 'H',
              }"
              :image-options="{
                hideBackgroundDots: true,
                imageSize: 0.4,
                margin: 0,
              }"
              :dots-options="{
                type: 'dots',
                color: '#26249a',
                gradient: {
                  type: 'linear',
                  rotation: 0,
                  colorStops: [
                    { offset: 0, color: '#26249a' },
                    { offset: 1, color: '#26249a' },
                  ],
                },
              }"
              :background-options="{ color: '#ffffff' }"
              :corners-square-options="{ type: 'dot', color: '#000000' }"
              :corners-dot-options="{ type: undefined, color: '#000000' }"
              :download="false"
            />
          </v-card>
          <div v-if="code" class="flex gap-2 justify-center mt-4">
            <v-btn elevation="1" @click="download">
              <AppIcon name="heroicons:arrow-down-tray-16-solid" />
              <span class="ml-1">Download</span>
            </v-btn>
          </div>
          <div
            v-if="message || $slots.message"
            class="mt-4 caption text-center"
          >
            <slot name="message">
              {{ message }}
            </slot>
          </div>
        </div>
      </div>
    </v-sheet>
  </v-dialog>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import QRCode from "qrcode-vue3";
import { kebabCase } from "lodash";

export default defineComponent({
  name: "QRCodeDialog",
  components: { QRCode },
  props: {
    code: { type: String },
    action: { type: String },
    actionNamespace: { type: String },
    title: { type: String },
    subtitle: { type: String },
    message: { type: String },
    fullscreen: { type: Boolean, default: false },
  },
  setup(props) {
    const dialog = ref(false);
    const qrCodeRef = ref<any>(null);

    const open = () => {
      dialog.value = true;
    };

    if (props.action) {
      const handle = props.actionNamespace
        ? useNamespacedEvent(props.actionNamespace).handle
        : useAppEvent().handle;

      handle(props.action, open);
    }

    const download = async () => {
      if (!qrCodeRef.value) {
        return;
      }

      const el = qrCodeRef.value.$el as HTMLElement | undefined;
      const img = el?.querySelector("img") as HTMLImageElement | undefined;

      if (!img?.src) {
        return;
      }

      const name = props.title ? kebabCase(props.title) : Date.now();

      const link = document.createElement("a");
      link.href = img.src;
      link.download = `qr-code-${name}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    return {
      dialog,
      open,
      download,
      qrCodeRef,
    };
  },
});
</script>
